import * as React from "react";
import { useDispatch } from "@teamprevent/admin-redux";
import { Form, Modal, Button, Loader } from "semantic-ui-react";
import { debounce } from "lodash";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import {
    MedicalType,
    medicalTypes as defaultMedicalTypes,
    faceToFaceMedicals
} from "@teamprevent/common/healthSurveillance/models";
import { SavingIndicator } from "@teamprevent/common/components/SavingIndicator";
import { SUBMIT_HEALTH_SURVEILLANCE_REVIEW } from "@teamprevent/common/questionnaires/components/healthSurveillance/review/actionTypes";

import { useOhCaseDetailsModel } from "../../../crud/ohCase";

import { useCustomMedicalTypes } from "../../../customMedicalTypes/hooks/useCustomMedicalTypes";

import { AddTasksInputs } from "../../../tasks/components/addTasksInputs";
import { TaskType, MedicalOutcomes, TaskModels } from "../../../task/model";
import {
    submitHealthSurveillanceReview,
    SubmitHealthSurveillanceReviewResult,
    saveHealthSurveillanceReview,
    loadSavedHealthSurveillanceReview
} from "../actions";

import { TasksApi } from "../../../tasks/tasksApi";
import { ReviewPpqDeclaration } from "../../../task/taskTypes";
import { MedicalOutcomeFormModel, adjustMedicalTypes, mapMedicalTypesToOutcomes } from "../models";

import { MedicalOutcomeForm } from "./MedicalOutcomeForm";
import { OutstandingPpqDeclarationMessage } from "./OutstandingPpqDeclarationMessage";

export const HealthSurveillanceReviewKey = "HealthSurveillanceReview";

interface OwnProps {
    customerId: string;
    ohCaseId: string;
    taskId: string;
    onClose: () => void;
}

export const HealthSurveillanceReviewForm: React.FunctionComponent<OwnProps> = (props) => {
    const dispatch = useDispatch();

    const { ohCaseId, customerId, taskId, onClose } = props;
    const [tasks, setTasks] = React.useState<TaskModels>([]);
    const [eTag, setETag] = React.useState<string | undefined>();
    const [submitted, setSubmitted] = React.useState(false);
    const [medicalOutcomes, setMedicalOutcomes] = React.useState<MedicalOutcomes>();
    const [successorTasks, setSuccessorTasks] = React.useState<TaskType[]>();
    const [saving, setSaving] = React.useState(false);
    const [loadComplete, setLoadComplete] = React.useState(false);

    const { ohCase, isLoaded: isOhCaseLoaded } = useOhCaseDetailsModel({ ohCaseId, customerId });
    const { customMedicalTypes, isLoaded: customMedicalsLoaded } = useCustomMedicalTypes();

    const mergedMedicalTypes = React.useMemo(() => {
        if (customMedicalsLoaded) {
            return [...defaultMedicalTypes, ...customMedicalTypes];
        }

        return [];
    }, [customMedicalTypes, customMedicalsLoaded]);

    const caseType = ohCase?.caseType;
    const reviewTask = isOhCaseLoaded ? ohCase.tasks?.find(t => t.id === taskId) : undefined;
    const outstandingPpqDeclarationReviews = isOhCaseLoaded ? ohCase.tasks.some(t =>
        t.type === ReviewPpqDeclaration.type && !t.completed && !t.cancelled) : false;

    const medicalTypesForOhCase = React.useMemo<MedicalType[]>(() => {
        if (!isOhCaseLoaded || !ohCase.medicalTypes ) {
            return [];
        }

        return ohCase.medicalTypes?.map(mid => {
            const matchedMedicalType = mergedMedicalTypes.find(m => m.id === mid);

            if (matchedMedicalType === undefined) {
                throw new Error(`Medical type with id ${mid} not found`);
            }

            return matchedMedicalType;

        }) ;
    }, [isOhCaseLoaded, mergedMedicalTypes, ohCase?.medicalTypes]);

    const loadSavedHealthSurveillanceReviewAction = React.useCallback(() => dispatch(loadSavedHealthSurveillanceReview(ohCaseId, taskId)),
        [dispatch, ohCaseId, taskId]);

    const getSuccessorTasks = React.useCallback(() => new TasksApi().getSuccessorTasks(ohCaseId, taskId), [ohCaseId, taskId]);

    const onSubmit = React.useCallback(async () => {
        if (!medicalOutcomes) {
            return;
        }

        setSubmitted(true);
        if (Object.values(medicalOutcomes).some(o => !o.valid)) {
            return;
        }

        if (tasks.some(t => !t.isValid)) {
            return;
        }

        const outcomes = medicalOutcomes.map(o => o.values);

        const addTaskFormModels = [];
        for (const task of tasks) {
            addTaskFormModels.push(task.task);
        }

        const submitResult: SubmitHealthSurveillanceReviewResult = await dispatch(
            submitHealthSurveillanceReview(ohCaseId, taskId, outcomes, addTaskFormModels));
        if (!submitResult.success) {
            return;
        }

        onClose();
    }, [dispatch, medicalOutcomes, ohCaseId, onClose, taskId, tasks]);

    const saveForm = React.useCallback(async (outcomes: MedicalOutcomes, tasksToAdd: TaskModels) => {
        const response = await dispatch(saveHealthSurveillanceReview(ohCaseId, taskId, outcomes, tasksToAdd, eTag ?? ""));
        setETag(response.eTag);
    },
    [dispatch, eTag, ohCaseId, taskId]);

    const onNextTasksChange = React.useCallback((updatedTasks: TaskModels) => {
        setTasks(updatedTasks);
    }, []);

    const getOnFormChange = React.useCallback(medicalOutcome => (model: MedicalOutcomeFormModel) => setMedicalOutcomes(
        medicalOutcomes.map(o =>
            o.medicalType === medicalOutcome.medicalType ? { ...model, medicalType: medicalOutcome.medicalType } : o
        )), [medicalOutcomes]);

    React.useEffect(() => {
        (async () => {
            const savedHealthSurveillanceReview = await loadSavedHealthSurveillanceReviewAction();
            if (savedHealthSurveillanceReview) {
                setTasks(savedHealthSurveillanceReview.tasksData?.tasks);
                setMedicalOutcomes(savedHealthSurveillanceReview.outcomesData?.outcomes);
                setETag(savedHealthSurveillanceReview.eTag);
            }
            setLoadComplete(true);
        })();
    }, [loadSavedHealthSurveillanceReviewAction]);

    React.useEffect(() => {
        (async () => {
            setSuccessorTasks(await getSuccessorTasks());
        })();
    }, [getSuccessorTasks]);

    React.useEffect(() => {
        // We don't want to show "FaceToFace" medicals as outcomes on the Review HS Form
        const filteredMedicalTypes = medicalTypesForOhCase.filter(o => !faceToFaceMedicals.includes(o.id));

        const medicalTypesToReview = adjustMedicalTypes(filteredMedicalTypes, reviewTask);
        setMedicalOutcomes(mapMedicalTypesToOutcomes(medicalTypesToReview));
    }, [customMedicalTypes, medicalTypesForOhCase, reviewTask]);

    const saveDebounced = debounce(async (
        saveOutcomes: MedicalOutcomes,
        saveTasks: TaskModels) => {
        if (!saveForm || saving) {
            return;
        }
        setSaving(true);
        try {
            await saveForm(saveOutcomes, saveTasks);
        } finally {
            setSaving(false);
        }
    }, 10000);

    React.useEffect(() => {
        if (!medicalOutcomes || !tasks || !loadComplete) {
            return undefined;
        }
        saveDebounced(medicalOutcomes, tasks);
        return saveDebounced.cancel;
    }, [medicalOutcomes, tasks, loadComplete, saveDebounced]);

    if (outstandingPpqDeclarationReviews) {
        return <OutstandingPpqDeclarationMessage onClose={onClose} />;
    }

    return (
        <>
            <Modal.Content>
                <Form>
                    {medicalOutcomes?.map(medicalOutcome =>
                        (<MedicalOutcomeForm
                            key={medicalOutcome.medicalType.id}
                            medicalType={medicalOutcome.medicalType}
                            model={medicalOutcome}
                            onChange={getOnFormChange(medicalOutcome)}
                            showErrors={submitted}
                        />)
                    )}
                    {successorTasks && medicalOutcomes && loadComplete ?
                        <AddTasksInputs
                            ohCaseId={props.ohCaseId}
                            onChange={onNextTasksChange}
                            showErrors={submitted}
                            taskTypes={successorTasks}
                            medicalTypes={medicalTypesForOhCase}
                            caseType={caseType}
                            savedTasks={tasks}
                            assignmentOptionRequired={true}
                            medicalOutcomes={medicalOutcomes}
                            filterMedicalTypes={true}
                        /> :
                        <Loader inline active />
                    }
                    <SavingIndicator display={saving} />
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button content="Close" onClick={onClose} />
                <IsLoadingButton requestNames={SUBMIT_HEALTH_SURVEILLANCE_REVIEW} content="Submit" onClick={onSubmit} />
            </Modal.Actions>
        </>
    );
};

